import React, { PureComponent } from 'react';
import { store } from 'redux-rest-reducer';
import { v4 as uuidv4 } from 'uuid';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Separator, Text, Row, Button } from '@cuemath/comps';
import { Card } from 'semantic-ui-react';
import {
  TEACHER_CONTROLS_TO_HIDE_TOOL_BAR,
  TILES_TO_HIDE,
  AWS_TIMER,
  RETRY_TIME,
  MATHIGON_ACTIVITY_QUERY,
} from '../constants';
import { openModal, closeModal } from 'actions/modal';
import { MODAL_FORM } from 'constants/form';
import COLORS from 'constants/colors';
import { Warning } from 'styled-icons/material/Warning';
import { patch as patchEditData } from 'actions/activities-add-card';
import {
  dataURIToBlob,
  jsonToBlob,
  handleAWSS3Upload,
  loadScript,
  getAWSSignedKey,
} from '../utils';

const FormWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: ${({ close }) => (close ? `0.1` : `1`)};
  pointer-events: ${({ close }) => (close ? `none` : `all`)};
`;

const CanvasContainer = styled.div`
  height: 100vh;
  max-height: 500px;
  opacity: ${({ isTransacting }) => (isTransacting ? 0.3 : 1)};
  pointer-events: ${({ isTransacting }) => (isTransacting ? 'none' : 'all')};
  position: relative;
  transition: opacity 0.3s ease-in;
  user-select: none;
  width: 100%;
  overflow: auto;
`;

const StyledRow = styled(Row)`
  height: 100%;
  position: relative;
  user-select: none;
  width: 100%;
`;

const WarningIcon = styled(Warning)`
  position: absolute;
  top: 40px;
  width: 40px;
  height: 40px;
  transform: translateY(-50%);
  margin: 0 auto;
  color: ${COLORS.COLA};
`;

const AlertCard = styled(Card)`
  position: absolute;
  height: 230px;
  min-width: 400px;
  margin: 0 auto;
  border: 1px solid rgba(0, 0, 0, 0.12);
  box-sizing: border-box;
  border-radius: 16px;
  z-index: 9999;
  opacity: 1;
  margin-bottom: 20px;
  pointer-events: all;
`;

const AlertModalRow = styled(Row)`
  padding: 20px;
  width: 400px;
  position: absolute;
`;

const AssignStyle = styled(Text)`
  width: 400px;
`;

const ButtonNo = styled.div`
  position: relative;
  background: #ffffff;
  border: 1.76087px solid #e2e4eb;
  box-sizing: border-box;
  border-radius: 24.6522px;
  width: 135px;
  height: 32px;
  cursor: pointer;
  margin-left: 10px;
`;

const ButtonYes = styled.div`
  position: relative;
  background: ${COLORS.CERULEAN};
  border-radius: 28px;
  width: 135px;
  height: 32px;
  cursor: pointer;
`;

const AlertStyle = styled(Row)`
  width: 240px;
  margin: 0 auto;
`;

const ExitText = styled(Text)`
  padding: 10px;
  margin-top: 60px;
`;

const NoText = styled.div`
  position: relative;
  font-weight: bold;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  color: ${({ color }) => color};
  display: flex;
  justify-content: center;
  top: 5px;
`;

const CenterAlignedDiv = styled(Text)`
  justify-content: center;
`;
let polypad;
const requestAnimationIds = {};
export const AWS_GET_SIGNEDKEY_TIMEOUT = {
  timer: AWS_TIMER, // 30mins
  id: null,
};

let editedRef;

class EditActivity extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      closeModal: false,
      polypadChanged: false,
      showConfirmationPopup: false,
    };
  }

  static propTypes = {
    actions: PropTypes.shape({
      postSubmitData: PropTypes.func.isRequired,
      closeModal: PropTypes.func.isRequired,
    }).isRequired,
    programs: PropTypes.array.isRequired,
    isEditActivity: PropTypes.shape({}).isRequired,
  };

  componentDidMount = async () => {
    const { activity } = this.props;
    const { learnosity_activity_ref: reference } = activity || '';
    const { awsSignedKey } = this.state;

    await this.downloadMathigonScript();
    await this.downloadMathigonPolyfillScript();

    if (!awsSignedKey || (awsSignedKey && awsSignedKey.isStale)) {
      const awsSignedKey = await getAWSSignedKey(MATHIGON_ACTIVITY_QUERY);

      this.setState({
        awsSignedKey,
      });
    }

    const jsonOBJ = await (await fetch(reference)).json();

    if (polypad) {
      polypad.unSerialize(jsonOBJ);
    }
  };

  componentWillUnmount() {
    Object.keys(requestAnimationIds).forEach(key => {
      window.cancelAnimationFrame(requestAnimationIds[key]);
      delete requestAnimationIds[key];
    });

    clearTimeout(this.mathigonScriptTimer);
    clearTimeout(this.mathigonPolyfillScriptTimer);

    // cleanup polypad
    if (polypad) {
      polypad.off('change', this.onPolypadChange);
    }

    polypad = null;
    this.unmounted = true;
  }

  onPolypadChange = () => {
    this.setState({
      polypadChanged: true,
    });
    document.getElementById('save_activity_button').style.display = 'block';
    document.getElementById('save_activity_button').style.color = '#fff';
    document.getElementById('save_activity_button').innerHTML = 'Save';
  };

  getAWSKeyOnEdit = (imageId, timestamp, fileType) => {
    //appending timestamp to png and json urls each time an activity is edited, because browser caches these urls
    const { awsSignedKey } = this.state;

    const { bucketName } = awsSignedKey;

    if (fileType === 'application/json') {
      const url = `https://${bucketName}.s3.amazonaws.com/`;
      const key = `media/wb-activity/${imageId}/activity_json${timestamp}`;

      editedRef = `${url}${key}`;

      return `media/wb-activity/${imageId}/activity_json${timestamp}`;
    }
    return `media/wb-activity/${imageId}/activity_thumbnail.png${timestamp}`;
  };

  handleFileUploadToS3OnCopy = (files, imageId, resolve, reject) => {
    const { awsSignedKey } = this.state;
    files.forEach(file => {
      const { type } = file;
      const key =
        type === 'application/json'
          ? `media/wb-activity/${imageId}/activity_json`
          : `media/wb-activity/${imageId}/activity_thumbnail.png`;

      handleAWSS3Upload(file, key, awsSignedKey, resolve, reject);
    });
  };

  handleFileUploadToS3 = (files, imageId, resolve, reject) => {
    const { awsSignedKey } = this.state;
    const timestamp = new Date().getTime();

    files.forEach(file => {
      const { type } = file;
      const key = this.getAWSKeyOnEdit(imageId, timestamp, type);

      handleAWSS3Upload(file, key, awsSignedKey, resolve, reject);
    });
  };

  onEditActivityClick = async () => {
    const arrBlobs = [];
    const { awsSignedKey } = this.state;
    const { bucketName } = awsSignedKey;
    const { activity, actions, getFilteredPrograms, programs } = this.props;
    const { learnosity_activity_ref: ref, id } = activity || {};

    await polypad.pngImage(200, 200).then(value => {
      const blobOBJ = dataURIToBlob(value);
      arrBlobs.push(blobOBJ);
    });

    const blobOBJJson = jsonToBlob(polypad.serialize());

    arrBlobs.push(blobOBJJson);

    const worksheetId = ref ? ref.split('/wb-activity/')[1].split('/activity')[0] : '';

    await this.handleActivityDataUpload(arrBlobs, worksheetId);
    actions.openModal(
      MODAL_FORM.ADD_NEW_ACTIVITY_MODAL,
      {
        blocking: true,
        size: 'custom',
        width: '835px',
        height: '600px',
      },
      {
        action: 'EDIT',
        isActivityPatch: true,
        programs: programs,
        isEditActivity: activity,
        getFilteredPrograms: getFilteredPrograms,
        worksheetId,
        blobs: arrBlobs,
        activity: activity,
        nodeId: id,
        bucketName,
        editedRef,
      },
    );
  };

  onCopyActivityClick = async () => {
    const arrBlobs = [];
    const { awsSignedKey } = this.state;
    const { bucketName } = awsSignedKey;
    const { actions, programs, activity, getFilteredPrograms } = this.props;
    const { id } = activity || {};

    await polypad.pngImage(200, 200).then(value => {
      const blobOBJ = dataURIToBlob(value);
      arrBlobs.push(blobOBJ);
    });

    const blobOBJJson = jsonToBlob(polypad.serialize());

    arrBlobs.push(blobOBJJson);

    const worksheetId = `${uuidv4()}`;

    actions.openModal(
      MODAL_FORM.ADD_NEW_ACTIVITY_MODAL,
      {
        blocking: true,
        size: 'custom',
        width: '835px',
        height: '600px',
      },
      {
        action: 'COPY',
        isActivityPatch: false,
        programs: programs,
        isEditActivity: activity,
        getFilteredPrograms: getFilteredPrograms,
        worksheetId,
        blobs: arrBlobs,
        activity: activity,
        nodeId: id,
        handleActivityDataUpload: this.handleImageUploadOnCopy,
        bucketName,
      },
    );
  };

  handleImageUploadOnCopy = async (files, imageId) => {
    return new Promise((resolve, reject) => {
      this.handleFileUploadToS3OnCopy(files, imageId, resolve, reject);
    });
  };

  handleActivityDataUpload = async (files, imageId) => {
    return new Promise((resolve, reject) => {
      this.handleFileUploadToS3(files, imageId, resolve, reject);
    });
  };

  updateToolBarUI = () => {
    const style = document.createElement('style');
    let styleHTML = '';

    TEACHER_CONTROLS_TO_HIDE_TOOL_BAR.forEach(key => {
      styleHTML += `.tool[title^="${key}"] { display: none; }`;
    });

    style.innerHTML = styleHTML;

    this.shadowRoot.appendChild(style);
  };

  updateSidebarUI = () => {
    TILES_TO_HIDE.forEach(key => {
      this.shadowRoot.querySelector(`[data-key="${key}"]`).style.display = 'none';
    });

    // to hide magic egg tile
    this.shadowRoot
      .querySelector(`[storage="polypad.tiles-geometry"]`)
      .querySelectorAll('section')[2]
      .querySelectorAll('div')[2].style.display = 'none';
    this.shadowRoot
      .querySelector(`[storage="polypad.tiles-geometry"]`)
      .querySelectorAll('section')[2]
      .querySelectorAll('div')[3].style.display = 'none';

    // to hide fractals
    this.shadowRoot
      .querySelector(`[storage="polypad.tiles-geometry"]`)
      .querySelectorAll('section')[6]
      .querySelectorAll('div')[17].style.display = 'none';
    this.shadowRoot
      .querySelector(`[storage="polypad.tiles-geometry"]`)
      .querySelectorAll('section')[6]
      .querySelectorAll('div')[18].style.display = 'none';

    // hide exploding dots
    this.shadowRoot
      .querySelector(`[storage="polypad.tiles-numbers"]`)
      .querySelectorAll('section')[6]
      .querySelectorAll('div')[4].style.display = 'none';
    this.shadowRoot
      .querySelector(`[storage="polypad.tiles-numbers"]`)
      .querySelectorAll('section')[6]
      .querySelectorAll('div')[5].style.display = 'none';

    // hide bucket of zeroes
    this.shadowRoot
      .querySelector(`[storage="polypad.tiles-numbers"]`)
      .querySelectorAll('section')[6]
      .querySelectorAll('div')[9].style.display = 'none';
    this.shadowRoot
      .querySelector(`[storage="polypad.tiles-numbers"]`)
      .querySelectorAll('section')[6]
      .querySelectorAll('div')[10].style.display = 'none';

    // hide powered by mathigon
    this.shadowRoot.querySelector(`[class="credit"]`).style.display = 'none';
    // hide caption in Kolam tiles
    this.shadowRoot.querySelectorAll(`[class="caption"]`)[1].style.display = 'none';
  };

  handleImageUploadBoardToS3 = (file, resolve, reject) => {
    console.log('TIMING: uploading started', new Date());

    const { awsSignedKey } = this.state;
    const { name } = file || '';

    const imageId = `${name}_${uuidv4()}`;
    const key = `media/wb-activity/${imageId}/tile_image.png`;

    handleAWSS3Upload(file, key, awsSignedKey, resolve, reject);
  };

  handleImageUploadBoard = async file => {
    return new Promise((resolve, reject) => {
      this.handleImageUploadBoardToS3(file, resolve, reject);
    });
  };

  initializePolypad = async () => {
    const { userId, isDemo } = this.props;

    const canvasContainer = document.getElementById('canvasContainer');

    polypad = await window.Polypad.create(canvasContainer, {
      apiKey: 'cuemath',
      userKey: isDemo ? `demo_${userId}` : userId,
      noPanAndZoom: true, // enable pan and zoom
      sidebar: true, // enable sidebar
      bindKeyboardEvents: true, // enable keyboard shortcuts
      imageUpload: this.handleImageUploadBoard,
    });

    this.shadowRoot = canvasContainer.shadowRoot;

    if (this.shadowRoot) {
      this.updateSidebarUI();
      this.updateToolBarUI();
    }

    polypad.toggleSidebar(false);
    polypad.on('change', this.onPolypadChange);
  };

  downloadMathigonScript = async () => {
    if (!window.Polypad) {
      await loadScript(
        `https://static.mathigon.org/api/polypad-v2.1.js`,
        'mathigon-script',
      )
        .then(() => {
          console.log('polypad script loaded successfully');
        })
        .catch(() => {
          if (!this.unmounted) {
            this.mathigonScriptTimer = setTimeout(
              this.downloadMathigonScript,
              RETRY_TIME,
            );
          }
        });
    }

    if (window.Polypad && !this.unmounted) {
      await this.initializePolypad();
    }
  };

  downloadMathigonPolyfillScript = () => {
    loadScript(`https://mathigon.org/polyfill.js`, 'mathigon-polyfill-script')
      .then(async () => {
        if (!this.unmounted) {
          console.log('polyfill script loaded successfully');
        }
      })
      .catch(() => {
        if (!this.unmounted) {
          this.mathigonPolyfillScriptTimer = setTimeout(
            this.downloadMathigonPolyfillScript,
            RETRY_TIME,
          );
        }
      });
  };

  onSaveActivityClick = async () => {
    const arrBlobs = [];
    const { activity, actions } = this.props;
    const { id: nodeId, learnosity_activity_ref: ref } = activity || {};
    const patchId = store.patch().id || '';

    await polypad.pngImage(200, 200).then(value => {
      const blobOBJ = dataURIToBlob(value);
      arrBlobs.push(blobOBJ);
    });

    const blobOBJJson = jsonToBlob(polypad.serialize());

    arrBlobs.push(blobOBJJson);

    const worksheetId = ref.split('/wb-activity/')[1].split('/activity')[0];

    await this.handleActivityDataUpload(arrBlobs, worksheetId);

    const patchObj = {
      action: 'UPDATE',
      action_data: { learnosity_activity_ref: editedRef },
    };

    actions.patchEditData(patchId, nodeId, patchObj);

    this.closeConfirmationModal();
  };

  onModalClose = () => {
    return (
      <AlertModalRow>
        <AlertCard>
          <AlertStyle justifyContent="center">
            <WarningIcon />
            <Separator height={15} transparent />
            <ExitText align="center">All unsaved changes will be lost.</ExitText>
            <Text align="center">Are you sure you want to close this window?</Text>
          </AlertStyle>
          <Separator height={15} transparent />
          <Row justifyContent="center">
            <ButtonYes onClick={() => this.onCloseModal()}>
              <NoText color={COLORS.WHITE}>Close</NoText>{' '}
            </ButtonYes>
            <ButtonNo onClick={() => this.onCloseClicked()}>
              <NoText color="#01A5F2">Cancel</NoText>
            </ButtonNo>
          </Row>
          <Separator height={20} transparent />
        </AlertCard>
        <Separator height={20} transparent />
      </AlertModalRow>
    );
  };

  onBackButtonClicked = async () => {
    const { actions } = this.props;
    const { polypadChanged, closeModal } = this.state;
    if (polypadChanged) {
      this.setState({ closeModal: !closeModal });
    }
    actions.closeModal();
  };

  closeConfirmationModal = () => {
    this.setState({
      showConfirmationPopup: false,
    });
  };

  openConfirmationModal = () => {
    this.setState({
      showConfirmationPopup: true,
    });
  };
  onConfirmationModalClose = () => {
    const { actions } = this.props;
    return (
      <AlertModalRow>
        <AlertCard>
          <AlertStyle justifyContent="center">
            <CenterAlignedDiv display="flex" align="center">
              <WarningIcon />
              <Separator height={15} transparent />
              <ExitText align="center">Changes will be saved.</ExitText>{' '}
            </CenterAlignedDiv>

            <Text align="center">Are you sure you want to update this activity?</Text>
          </AlertStyle>
          <Separator height={15} transparent />
          <Row justifyContent="center">
            <ButtonYes onClick={actions.closeModal}>
              <NoText color={COLORS.WHITE}>Close</NoText>{' '}
            </ButtonYes>
            <ButtonNo onClick={this.onSaveActivityClick}>
              <NoText color={COLORS.SKY}>Save</NoText>
            </ButtonNo>
          </Row>
          <Separator height={20} transparent />
        </AlertCard>
        <Separator height={20} transparent />
      </AlertModalRow>
    );
  };

  render() {
    const { closeModal, showConfirmationPopup } = this.state;

    return (
      <FormWrapper>
        {showConfirmationPopup && this.onConfirmationModalClose()}
        <StyledRow>
          <div
            style={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}
          >
            <Button
              label="Back"
              style={{ margin: '20px' }}
              onClick={this.onBackButtonClicked}
            />
            <div style={{ display: 'flex' }}>
              <Button
                id="save_activity_button"
                label="Save"
                style={{ display: 'none', margin: '20px' }}
                onClick={this.openConfirmationModal}
              />
              <Button
                id="edit_activity_button"
                label="Edit"
                style={{ margin: '20px' }}
                onClick={this.onEditActivityClick}
              />
              <Button
                id="copy_activity_button"
                label="Copy"
                style={{ margin: '20px' }}
                onClick={this.onCopyActivityClick}
              />
            </div>
          </div>

          <CanvasContainer id="canvasContainer" />
          {closeModal && this.onModalClose()}
        </StyledRow>
      </FormWrapper>
    );
  }
}

export default connect(null, dispatch => ({
  actions: bindActionCreators(
    {
      closeModal,
      openModal,
      patchEditData,
    },
    dispatch,
  ),
}))(EditActivity);
